import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import { WorkspaceProduct } from 'features/product/types/workspaceProduct'
import { updateUser, useAuthUser } from 'features/user'
import { mapGraphQLErrors } from 'providers/graphql'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { Schema } from '../../components/TrialDialog/schema'
import { fromDTO, toDTO } from './api/mapper'
import { UPDATE_PRODUCT } from './api/mutation'
import { Data, VariablesDTO } from './api/types'
import { UseUpdateProduct, Variables } from './types'
import { updateWorkspaceProducts } from './utils/updateWorkspaceProducts'

export const useUpdateProduct = ({ product }: WorkspaceProduct): UseUpdateProduct => {
  const { t } = useTranslation('features/product')
  const { enqueueSnackbar } = useSnackbar()
  const [errors, setErrors] = useState<FieldErrors<Schema>>({})
  const dispatch = useDispatch()
  const {
    user: { workspaceProducts: userWorkspaceProducts },
  } = useAuthUser()

  const { mutateAsync } = useMutation<{ input: VariablesDTO }, Data>({
    mutation: UPDATE_PRODUCT,
    onCompleted: async data => {
      const {
        updateWorkspaceProductStatus: { errors, workspaceProduct },
      } = data
      const productName = t(`product.${product}`)

      if (errors && errors.length > 0) {
        setErrors(mapGraphQLErrors(errors))
        enqueueSnackbar(t('notification.update.error', { product: productName }), {
          variant: 'error',
        })
      } else if (workspaceProduct && userWorkspaceProducts) {
        dispatch(
          updateUser({
            workspaceProducts: updateWorkspaceProducts(
              fromDTO(workspaceProduct),
              userWorkspaceProducts,
            ),
          }),
        )
        enqueueSnackbar(t('notification.update.success', { product: productName }), {
          variant: 'success',
        })
      }
    },
    queriesToInvalidate: ['getWorkspaceProducts'],
  })

  const update = (variables: Variables): void => {
    void mutateAsync({
      input: toDTO(variables),
    })
  }

  return {
    errors,
    update,
  }
}
